import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Badge } from "../components/ui/Badge";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import { apiService } from "../services/api";
import toast from "react-hot-toast";
import {
  PlusIcon,
  EnvelopeIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

const StaffManagement = () => {
  const { clubId } = useParams();
  const [staff, setStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [staffToRemove, setStaffToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: "evaluator",
  });
  const [inviting, setInviting] = useState(false);
  const [staffLimitInfo, setStaffLimitInfo] = useState(null); // { current, limit }

  const loadStaff = useCallback(async () => {
    try {
      const staffData = await apiService.getClubStaff(clubId);
      setStaff(staffData);
    } catch (error) {
      console.error("Error loading staff:", error);
      toast.error("Failed to load staff");
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    loadStaff();
    apiService.getClubSubscriptionLimits(clubId)
      .then((limits) => setStaffLimitInfo({ current: limits.staff.current, limit: limits.staff.limit }))
      .catch((err) => console.warn("Could not load subscription limits:", err));
  }, [loadStaff, clubId]);

  const handleInviteStaff = async (e) => {
    e.preventDefault();
    setInviting(true);

    try {
      await apiService.inviteStaff(clubId, inviteForm);
      setShowInviteModal(false);
      setInviteForm({ email: "", name: "", role: "evaluator" });
      await loadStaff();
      toast.success("Staff member invited successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to invite staff member");
    }

    setInviting(false);
  };

  const handleRemoveStaff = async () => {
    if (!staffToRemove) return;

    setRemoving(true);
    try {
      await apiService.removeStaff(clubId, staffToRemove.id);
      await loadStaff();
      setShowRemoveModal(false);
      setStaffToRemove(null);
      toast.success("Staff member removed successfully");
    } catch (error) {
      toast.error("Failed to remove staff member");
    }
    setRemoving(false);
  };

  const handleRemoveStaffClick = (staff) => {
    setStaffToRemove(staff);
    setShowRemoveModal(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "club_admin":
        return "blue";
      case "coach":
        return "green";
      case "evaluator":
        return "yellow";
      default:
        return "gray";
    }
  };

  const getRoleLabel = (role) => {
    return role.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Filter staff based on search term
  const filteredStaff = staff.filter((member) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      member.name.toLowerCase().includes(searchLower) ||
      member.email.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-gray-600">
            {staffLimitInfo?.current ?? staff.length}
            {staffLimitInfo?.limit != null ? ` / ${staffLimitInfo.limit}` : ""} staff member{staff.length === 1 ? "" : "s"}
            {searchTerm && (
              <span>
                {" "}
                • {filteredStaff.length} matching "{searchTerm}"
              </span>
            )}
          </p>
        </div>
        {(() => {
          const limit = staffLimitInfo?.limit;
          const current = staffLimitInfo?.current ?? staff.length;
          const atLimit = limit != null && current >= limit;
          const tooltip = atLimit
            ? `Your plan allows ${limit} staff member${limit === 1 ? "" : "s"}. Upgrade your plan to add more.`
            : undefined;
          return (
            <div className="relative group">
              <Button
                onClick={atLimit ? undefined : () => setShowInviteModal(true)}
                disabled={atLimit}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Invite Staff
              </Button>
              {atLimit && (
                <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                  {tooltip}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Search Bar */}
      {staff.length > 0 && (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <Input
            type="text"
            placeholder="Search staff by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {/* Staff List */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Team Members ({filteredStaff.length}
            {searchTerm &&
              filteredStaff.length !== staff.length &&
              ` of ${staff.length}`}
            )
          </h2>

          {staff.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-2xl">👥</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No staff members yet
              </h3>
              <p className="text-gray-600 mb-4">
                Invite coaches and evaluators to help manage your club.
              </p>
              <Button
                onClick={() => setShowInviteModal(true)}
                disabled={staffLimitInfo?.limit != null && (staffLimitInfo?.current ?? 0) >= staffLimitInfo.limit}
              >
                Invite First Staff Member
              </Button>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MagnifyingGlassIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No staff members found
              </h3>
              <p className="text-gray-600 mb-4">
                No staff members match your search for "{searchTerm}".
              </p>
              <Button variant="outline" onClick={() => setSearchTerm("")}>
                Clear Search
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredStaff.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-gray-600 font-semibold text-lg">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {member.name}
                      </h3>
                      <p className="text-gray-600">{member.email}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant={getRoleColor(member.role)}>
                          {getRoleLabel(member.role)}
                        </Badge>
                        {member.temp_password && (
                          <Badge variant="orange">Pending Setup</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {member.temp_password && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          // Resend invitation logic
                          toast.success("Invitation resent!");
                        }}
                      >
                        <EnvelopeIcon className="w-4 h-4 mr-1" />
                        Resend
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleRemoveStaffClick(member)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Invite Staff Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite Staff Member"
      >
        <form onSubmit={handleInviteStaff} className="space-y-4">
          <Input
            placeholder="Full Name"
            value={inviteForm.name}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, name: e.target.value })
            }
            required
          />

          <Input
            type="email"
            placeholder="Email Address"
            value={inviteForm.email}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, email: e.target.value })
            }
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
              value={inviteForm.role}
              onChange={(e) =>
                setInviteForm({ ...inviteForm, role: e.target.value })
              }
            >
              <option value="coordinator">
                Coordinator - Can check in athletes and view assign numbers
              </option>
              <option value="evaluator">
                Evaluator - Can participate in evaluations
              </option>
              <option value="coach">
                Coach - Can manage players and evaluations
              </option>
              <option value="club_admin">
                Club Admin - Full club management access
              </option>
            </select>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-slate-900 mb-1">
              What happens next?
            </h4>
            <ul className="text-sm text-slate-800 space-y-1">
              <li>• An invitation email will be sent</li>
              <li>• They'll receive temporary login credentials</li>
              <li>• They can access evaluations immediately after setup</li>
            </ul>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowInviteModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={inviting || !inviteForm.name || !inviteForm.email}
            >
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Remove Staff Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveModal}
        onConfirm={handleRemoveStaff}
        onCancel={() => {
          setShowRemoveModal(false);
          setStaffToRemove(null);
        }}
        title="Remove Staff Member"
        message={`Are you sure you want to remove "${staffToRemove?.name}" from your club? They will lose access to all club data and evaluations.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={removing}
        type="danger"
      />
    </div>
  );
};

export default StaffManagement;
